import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { FormValue, MessagesSetFormContent } from './components/MessagesSetFormContent';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { MessagesSetState, useCreateMessagesSetMutation, TotadminPermission } from 'data/__generated__';

export const MessagesSetCreate = () => {
    const navigate = useNavigate();
    const [createMessagesSet, { loading: createMessagesSetLoading }] = useCreateMessagesSetMutation();

    const [formValue, setFormValue] = useState<FormValue>({
        name: '',
        messages: [],
    });

    async function onSubmit() {
        const variables = {
            messagesSetInput: {
                ...formValue,
                messages: formValue.messages.map((message) => {
                    return { ...message, redirectUrl: message.redirectUrl || null };
                }),
                state: MessagesSetState.Regular,
            },
        };
        const { data } = await createMessagesSet({ variables });

        if (data) {
            const {
                createMessagesSet: { createdMessagesSet, success, errors },
            } = data;
            if (success && createdMessagesSet) {
                toast.success('La série a bien été créée !');
                navigate(`/messages-set/${createdMessagesSet._id}`);
            }
            if (errors) {
                errors.forEach((error) => {
                    toast.error(error, { autoClose: false });
                });

                if (errors.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    }

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/messages-sets' }],
        },
    ];

    return (
        <Container>
            <Form>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.messagesSetsCreate} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={actionGroups}
                        onSave={onSubmit}
                        saveLoading={createMessagesSetLoading}
                        saveTitle="Créer"
                        saveRequiredPermissions={[TotadminPermission.MessagesSetsManage]}
                    />
                </Header>
                <Content>
                    <ScrollableContent>
                        <MessagesSetFormContent formValue={formValue} setFormValue={setFormValue} />
                    </ScrollableContent>
                </Content>
            </Form>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
